import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import {
  getMyProjectDigestPreference,
  getProject,
  getProjectDigestSettings,
  updateMyProjectDigestPreference,
  updateProjectDigestSettings,
  uploadProjectImage,
  addProjectMember,
  removeProjectMember,
  updateProject,
  deleteProject,
} from '../../api/projects';
import { createVibe, deleteVibe, updateVibe, uploadVibeImage } from '../../api/vibes';
import { createCut, deleteCut, reorderCuts, updateCut, moveCut } from '../../api/cuts';
import { getUsers } from '../../api/users';
import { Project, User, Cut, ProjectDigestConfig } from '../../types';
import { Button } from '../../components/ui/Button';
import { Card, CardImage } from '../../components/ui/Card';
import { ActionSheet } from '../../components/ui/ActionMenu';
import { SideSheet } from '../../components/ui/Modal';
import { Input, Textarea } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';
import { VibeCard } from '../../components/vibes/VibeCard';
import { ImageUploadSheet } from '../../components/files/ImageUploadSheet';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const digestFrequencyOptions = [
    { value: 15, label: 'Every 15 minutes' },
    { value: 30, label: 'Every 30 minutes' },
    { value: 60, label: 'Hourly' },
    { value: 180, label: 'Every 3 hours' },
    { value: 360, label: 'Every 6 hours' },
    { value: 720, label: 'Every 12 hours' },
    { value: 1440, label: 'Daily' },
    { value: 10080, label: 'Weekly' },
  ];
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px drag before activating (prevents accidental drags)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showVibeModal, setShowVibeModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editProjectName, setEditProjectName] = useState('');
  const [showEmailDigestsSheet, setShowEmailDigestsSheet] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [vibeForm, setVibeForm] = useState({ name: '', theme: '', notes: '' });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activeDragCut, setActiveDragCut] = useState<Cut | null>(null);
  const [digestConfig, setDigestConfig] = useState<ProjectDigestConfig | null>(null);
  const [digestEnabledDraft, setDigestEnabledDraft] = useState(false);
  const [digestFrequencyDraft, setDigestFrequencyDraft] = useState(1440);
  const [digestOptedOut, setDigestOptedOut] = useState(false);
  const [isDigestSaving, setIsDigestSaving] = useState(false);
  const [isDigestPrefSaving, setIsDigestPrefSaving] = useState(false);
  const [digestError, setDigestError] = useState('');
  const [vibeSortBy, setVibeSortBy] = useState<'default' | 'name' | 'cuts' | 'recent'>('default');
  const [cutSortBy, setCutSortBy] = useState<'default' | 'name' | 'recent' | 'bpm'>('default');
  const [showAllCuts, setShowAllCuts] = useState(false);

  type VibeSortOption = 'default' | 'name' | 'cuts' | 'recent';
  type CutSortOption = 'default' | 'name' | 'recent' | 'bpm';

  const getSortedVibes = () => {
    if (!project?.vibes) return [];
    const vibes = [...project.vibes];
    switch (vibeSortBy) {
      case 'name':
        return vibes.sort((a, b) => a.name.localeCompare(b.name));
      case 'cuts':
        return vibes.sort((a, b) => (b.cuts?.length || 0) - (a.cuts?.length || 0));
      case 'recent':
        return vibes.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      default:
        return vibes;
    }
  };

  const sortedVibes = getSortedVibes();

  // Flatten all cuts from all vibes
  const getAllCuts = (): (Cut & { vibeName: string })[] => {
    if (!project?.vibes) return [];
    return project.vibes.flatMap((vibe) =>
      (vibe.cuts || []).map((cut) => ({ ...cut, vibeName: vibe.name }))
    );
  };

  const getSortedAllCuts = () => {
    const allCuts = getAllCuts();
    switch (cutSortBy) {
      case 'name':
        return allCuts.sort((a, b) => a.name.localeCompare(b.name));
      case 'recent':
        return allCuts.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      case 'bpm':
        return allCuts.sort((a, b) => (a.bpm ?? 0) - (b.bpm ?? 0));
      default:
        return allCuts;
    }
  };

  const sortedAllCuts = getSortedAllCuts();

  const fetchProject = async () => {
    if (!id) return;
    try {
      const data = await getProject(id);
      setProject(data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      navigate('/projects');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDigestData = async () => {
    if (!id) return;

    try {
      const preference = await getMyProjectDigestPreference(id);
      setDigestOptedOut(preference.optedOut);
    } catch (err) {
      console.error('Failed to fetch digest preference:', err);
    }

    if (isAdmin) {
      try {
        const config = await getProjectDigestSettings(id);
        setDigestConfig(config);
        setDigestEnabledDraft(config.enabled);
        setDigestFrequencyDraft(config.frequencyMinutes);
      } catch (err) {
        console.error('Failed to fetch digest settings:', err);
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchDigestData();
  }, [id, isAdmin]);

  useEffect(() => {
    const handleProjectDataChanged = (event: Event) => {
      const custom = event as CustomEvent<{ projectId?: string }>;
      if (!id) return;
      if (!custom.detail?.projectId || custom.detail.projectId === id) {
        fetchProject();
      }
    };

    window.addEventListener('bandmate:project-data-changed', handleProjectDataChanged);
    return () => window.removeEventListener('bandmate:project-data-changed', handleProjectDataChanged);
  }, [id]);

  const handleSaveDigestSettings = async () => {
    if (!id || !isAdmin) return;

    setIsDigestSaving(true);
    setDigestError('');
    try {
      const updated = await updateProjectDigestSettings(id, {
        enabled: digestEnabledDraft,
        frequencyMinutes: digestFrequencyDraft,
      });
      setDigestConfig(updated);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setDigestError(apiError.response?.data?.error || 'Failed to update digest settings');
    } finally {
      setIsDigestSaving(false);
    }
  };

  const handleUpdateDigestPreference = async (optedOut: boolean) => {
    if (!id) return;

    setIsDigestPrefSaving(true);
    setDigestError('');
    try {
      const updated = await updateMyProjectDigestPreference(id, optedOut);
      setDigestOptedOut(updated.optedOut);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setDigestError(apiError.response?.data?.error || 'Failed to update digest preference');
    } finally {
      setIsDigestPrefSaving(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!id) return;
    await uploadProjectImage(id, file);
    fetchProject();
  };

  const handleEditProject = async () => {
    if (!editProjectName.trim() || !id) {
      setError('Project name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await updateProject(id, editProjectName);
      setShowEditProjectModal(false);
      fetchProject();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to update project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!id || !confirm('Are you sure you want to delete this project and all its vibes? This cannot be undone.')) return;
    
    try {
      await deleteProject(id);
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  const handleCreateVibe = async () => {
    if (!vibeForm.name.trim() || !id) {
      setError('Vibe name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await createVibe(id, vibeForm);
      setVibeForm({ name: '', theme: '', notes: '' });
      setShowVibeModal(false);
      fetchProject();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to create vibe');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVibe = async (vibeId: string) => {
    try {
      await deleteVibe(vibeId);
      fetchProject();
    } catch (error) {
      console.error('Failed to delete vibe:', error);
    }
  };

  const handleUpdateVibe = async (vibeId: string, data: { name?: string; theme?: string; notes?: string }) => {
    await updateVibe(vibeId, data);
    fetchProject();
  };

  const handleUploadVibeImage = async (vibeId: string, file: File) => {
    await uploadVibeImage(vibeId, file);
    fetchProject();
  };

  const handleCreateCut = async (vibeId: string, data: { name: string; bpm?: number; timeSignature?: string }) => {
    await createCut(vibeId, data);
    fetchProject();
  };

  const handleDeleteCut = async (cutId: string) => {
    await deleteCut(cutId);
    fetchProject();
  };

  const handleUpdateCut = async (cutId: string, data: { name?: string; bpm?: number | null; timeSignature?: string | null }) => {
    await updateCut(cutId, data);
    fetchProject();
  };

  const handleMoveCut = async (cutId: string, targetVibeId: string) => {
    await moveCut(cutId, targetVibeId);
    fetchProject();
  };

  // Find which vibe a cut belongs to
  const findCutVibe = (cutId: string) => {
    return project?.vibes?.find(vibe => vibe.cuts?.some(cut => cut.id === cutId));
  };

  // Find a cut by ID across all vibes
  const findCut = (cutId: string): Cut | undefined => {
    for (const vibe of project?.vibes || []) {
      const cut = vibe.cuts?.find(c => c.id === cutId);
      if (cut) return cut;
    }
    return undefined;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const cut = findCut(active.id as string);
    setActiveDragCut(cut || null);
  };

  const handleDragCancel = () => {
    setActiveDragCut(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragCut(null);

    if (!over || !project) return;

    const activeCutId = active.id as string;
    const overId = over.id as string;

    // Find source vibe (where the cut is coming from)
    const sourceVibe = findCutVibe(activeCutId);
    if (!sourceVibe) return;

    // Check if dropped on a vibe (for cross-vibe move)
    const targetVibe = project.vibes?.find(v => v.id === overId);
    
    if (targetVibe && targetVibe.id !== sourceVibe.id) {
      // Cross-vibe move: dropped on a different vibe
      const cut = findCut(activeCutId);
      if (!cut) return;

      // Optimistic update
      const updatedVibes = project.vibes?.map(vibe => {
        if (vibe.id === sourceVibe.id) {
          // Remove from source
          return { ...vibe, cuts: vibe.cuts?.filter(c => c.id !== activeCutId) };
        }
        if (vibe.id === targetVibe.id) {
          // Add to target
          return { ...vibe, cuts: [...(vibe.cuts || []), cut] };
        }
        return vibe;
      });
      setProject({ ...project, vibes: updatedVibes });

      // API call
      try {
        await moveCut(activeCutId, targetVibe.id);
      } catch (error) {
        console.error('Failed to move cut:', error);
        fetchProject(); // Revert on error
      }
      return;
    }

    // Check if dropped on another cut (for reordering within same vibe or cross-vibe)
    const overCutVibe = findCutVibe(overId);
    
    if (overCutVibe) {
      if (overCutVibe.id === sourceVibe.id) {
        // Same vibe reordering
        const oldIndex = sourceVibe.cuts?.findIndex(cut => cut.id === activeCutId) ?? -1;
        const newIndex = sourceVibe.cuts?.findIndex(cut => cut.id === overId) ?? -1;

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const newCuts = [...(sourceVibe.cuts || [])];
          const [removed] = newCuts.splice(oldIndex, 1);
          newCuts.splice(newIndex, 0, removed);
          
          const cutIds = newCuts.map(cut => cut.id);

          // Optimistic update
          const updatedVibes = project.vibes?.map(vibe => {
            if (vibe.id === sourceVibe.id) {
              return { ...vibe, cuts: newCuts };
            }
            return vibe;
          });
          setProject({ ...project, vibes: updatedVibes });

          // API call
          try {
            await reorderCuts(sourceVibe.id, cutIds);
          } catch (error) {
            console.error('Failed to reorder cuts:', error);
            fetchProject();
          }
        }
      } else {
        // Cross-vibe move: dropped on a cut in a different vibe
        const cut = findCut(activeCutId);
        if (!cut) return;

        // Optimistic update
        const updatedVibes = project.vibes?.map(vibe => {
          if (vibe.id === sourceVibe.id) {
            return { ...vibe, cuts: vibe.cuts?.filter(c => c.id !== activeCutId) };
          }
          if (vibe.id === overCutVibe.id) {
            const newCuts = [...(vibe.cuts || [])];
            const overIndex = newCuts.findIndex(c => c.id === overId);
            newCuts.splice(overIndex, 0, cut);
            return { ...vibe, cuts: newCuts };
          }
          return vibe;
        });
        setProject({ ...project, vibes: updatedVibes });

        // API call
        try {
          await moveCut(activeCutId, overCutVibe.id);
        } catch (error) {
          console.error('Failed to move cut:', error);
          fetchProject();
        }
      }
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !id) return;

    setIsSubmitting(true);
    try {
      await addProjectMember(id, selectedUserId);
      setSelectedUserId('');
      setShowMemberModal(false);
      fetchProject();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to add member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    const member = project?.members?.find((m) => m.userId === userId);
    const memberName = member?.user?.name || 'this member';
    if (!id) return;

    try {
      await removeProjectMember(id, userId);
      fetchProject();
      toast.info(`${memberName} removed`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await addProjectMember(id, userId);
              fetchProject();
              toast.success(`${memberName} restored`);
            } catch {
              toast.error('Failed to undo');
            }
          },
        },
      });
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('Failed to remove member');
    }
  };

  const openMemberModal = () => {
    fetchUsers();
    fetchProject(); // Refresh members to avoid stale available users list
    setShowMemberModal(true);
  };

  const availableUsers = users.filter(
    (u) => !project?.members?.some((m) => m.userId === u.id)
  );

  const projectActionMenuItems = [
    {
      label: 'Edit Project',
      description: 'Update project details',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => {
        setEditProjectName(project?.name || '');
        setShowEditProjectModal(true);
      },
      visible: isAdmin,
    },
    {
      label: 'Upload Image',
      description: 'Add or change cover image',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      onClick: () => setShowImageUpload(true),
      visible: isAdmin,
    },
    {
      label: 'Project Settings',
      description: 'Configure project digest settings',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: () => setShowEmailDigestsSheet(true),
      visible: true,
    },
    {
      label: 'Delete Project',
      description: 'Permanently remove this project',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      onClick: handleDeleteProject,
      variant: 'danger' as const,
      visible: isAdmin,
    },
  ];

  if (isLoading) {
    return <Loading className="py-12" />;
  }

  if (!project) {
    return (
      <Card className="text-center py-12">
        <p className="text-muted">Project not found</p>
        <Link to="/projects" className="text-primary hover:underline mt-2 inline-block">
          Back to projects
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto w-full">
      {/* Breadcrumb */}
      <nav className="text-sm text-muted">
        <Link to="/projects" className="hover:text-text">Projects</Link>
        <span className="mx-2">/</span>
        <span className="text-text">{project.name}</span>
      </nav>

      {/* Project Header */}
      <div className="flex flex-col md:flex-row chat-expanded:!flex-col gap-6">
        {/* Title - show first when chat expanded */}
        <div className="hidden chat-expanded:block">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-text">{project.name}</h1>
            <ActionSheet items={projectActionMenuItems} title="Project Actions" />
          </div>
        </div>

        <div className="relative w-full md:w-64 chat-expanded:!w-full flex-shrink-0">
          <CardImage src={project.image} alt={project.name} className="aspect-square" fit="scale-down" />
        </div>

        <div className="flex-1">
          {/* Title - show here normally, hide when chat expanded */}
          <div className="flex items-start justify-between gap-4 chat-expanded:!hidden">
            <h1 className="text-2xl md:text-3xl font-bold text-text">{project.name}</h1>
            <ActionSheet items={projectActionMenuItems} title="Project Actions" />
          </div>
          
          {/* Members */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted">Members</span>
              {isAdmin && (
                <button
                  onClick={openMemberModal}
                  className="text-primary hover:text-primary-hover text-sm font-medium"
                >
                  Add member
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {project.members?.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-2 px-3 py-1.5 bg-surface-light rounded-full"
                >
                  <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-primary text-xs font-medium">
                      {member.user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm text-text">{member.user.name}</span>
                  {isAdmin && (
                    <button
                      onClick={() => handleRemoveMember(member.userId)}
                      className="text-muted hover:text-error transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {(!project.members || project.members.length === 0) && (
                <span className="text-sm text-muted">No members yet</span>
              )}
            </div>
          </div>

          {/* Email Digest Preferences - moved to Email Digests side sheet */}
        </div>
      </div>

      {/* All Cuts Section */}
      {sortedAllCuts.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowAllCuts(!showAllCuts)}
            className="flex items-center gap-2 text-left group"
            aria-expanded={showAllCuts}
          >
            <h2 className="text-xl font-semibold text-text group-hover:text-primary transition-colors">
              All Cuts
            </h2>
            <span className="text-sm text-muted">({sortedAllCuts.length})</span>
            <svg
              className={`w-4 h-4 text-muted transition-transform ${showAllCuts ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAllCuts && (
            <>
              <div className="flex items-center gap-3 mt-4">
                <select
                  value={cutSortBy}
                  onChange={(e) => setCutSortBy(e.target.value as CutSortOption)}
                  className="px-3 py-1.5 bg-surface-light border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                >
                  <option value="default">Default order</option>
                  <option value="name">Sort by name</option>
                  <option value="recent">Recently updated</option>
                  <option value="bpm">Sort by BPM</option>
                </select>
              </div>

              <div className="space-y-2 mt-4">
            {sortedAllCuts.map((cut) => (
              <Link
                key={cut.id}
                to={`/cuts/${cut.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-surface-light hover:bg-primary/10 transition-colors group"
              >
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-text group-hover:text-primary transition-colors truncate">
                      {cut.name}
                    </h4>
                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-surface border border-border text-muted flex-shrink-0">
                      {cut.vibeName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {cut.bpm && (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
                        {cut.bpm} BPM
                      </span>
                    )}
                    {cut.timeSignature && (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-secondary/20 text-secondary-foreground">
                        {cut.timeSignature}
                      </span>
                    )}
                  </div>
                </div>
                <svg className="w-5 h-5 text-muted group-hover:text-primary transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
            </>
          )}
        </div>
      )}

      {/* Vibes Section */}
      <div>
        <div className="flex items-center justify-between mb-4 mt-8">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-text">Vibes</h2>
            <select
              value={vibeSortBy}
              onChange={(e) => setVibeSortBy(e.target.value as VibeSortOption)}
              className="px-3 py-1.5 bg-surface-light border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
            >
              <option value="default">Default order</option>
              <option value="name">Sort by name</option>
              <option value="cuts">Sort by cuts</option>
              <option value="recent">Recently updated</option>
            </select>
          </div>
          <Button onClick={() => setShowVibeModal(true)} size="sm">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Vibe
          </Button>
        </div>

        {project.vibes?.length === 0 ? (
          <Card className="text-center py-12">
            <svg className="w-12 h-12 text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="text-muted">No vibes yet</p>
            <p className="text-sm text-muted mt-1">Create a vibe to start organizing your music</p>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 chat-expanded:!grid-cols-1 gap-6">
              {sortedVibes.map((vibe) => (
                <VibeCard
                  key={vibe.id}
                  vibe={vibe}
                  allVibes={sortedVibes}
                  onCreateCut={handleCreateCut}
                  onDeleteCut={handleDeleteCut}
                  onUpdateCut={handleUpdateCut}
                  onMoveCut={handleMoveCut}
                  onEditVibe={handleUpdateVibe}
                  onDeleteVibe={handleDeleteVibe}
                  onUploadImage={handleUploadVibeImage}
                />
              ))}
            </div>
            <DragOverlay>
              {activeDragCut ? (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-surface border border-primary shadow-lg">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-text truncate">{activeDragCut.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      {activeDragCut.bpm && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
                          {activeDragCut.bpm} BPM
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Create Vibe Side Sheet */}
      <SideSheet
        isOpen={showVibeModal}
        onClose={() => {
          setShowVibeModal(false);
          setVibeForm({ name: '', theme: '', notes: '' });
          setError('');
        }}
        title="Create New Vibe"
        description="Add a new vibe to organize your music"
        size="md"
        footer={
          <Button onClick={handleCreateVibe} isLoading={isSubmitting}>
            Create Vibe
          </Button>
        }
      >
        <div className="space-y-4">
          <Input
            label="Vibe Name"
            placeholder="Enter vibe name"
            value={vibeForm.name}
            onChange={(e) => setVibeForm({ ...vibeForm, name: e.target.value })}
            error={error}
          />
          <Input
            label="Theme (optional)"
            placeholder="e.g., Summer vibes, Dark ambient"
            value={vibeForm.theme}
            onChange={(e) => setVibeForm({ ...vibeForm, theme: e.target.value })}
          />
          <Textarea
            label="Notes (optional)"
            placeholder="Add any notes about this vibe..."
            value={vibeForm.notes}
            onChange={(e) => setVibeForm({ ...vibeForm, notes: e.target.value })}
            rows={3}
          />
        </div>
      </SideSheet>

      {/* Add Member Side Sheet */}
      <SideSheet
        isOpen={showMemberModal}
        onClose={() => {
          setShowMemberModal(false);
          setSelectedUserId('');
          setError('');
        }}
        title="Add Member"
        description="Select a user to add to this project"
        footer={
          <Button onClick={handleAddMember} isLoading={isSubmitting} disabled={!selectedUserId}>
            Add Member
          </Button>
        }
      >
        <div className="space-y-4">
          {availableUsers.length === 0 ? (
            <p className="text-muted text-center py-4">All users are already members of this project</p>
          ) : (
            <div className="space-y-2">
              {availableUsers.map((user) => (
                <label
                  key={user.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedUserId === user.id
                      ? 'bg-primary/10 border border-primary'
                      : 'bg-surface-light border border-transparent hover:border-border'
                  }`}
                >
                  <input
                    type="radio"
                    name="user"
                    value={user.id}
                    checked={selectedUserId === user.id}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="hidden"
                  />
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-primary font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-text">{user.name}</p>
                    <p className="text-sm text-muted">{user.email}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
          {error && <p className="text-error text-sm">{error}</p>}
        </div>
      </SideSheet>

      {/* Project Image Upload Side Sheet */}
      <ImageUploadSheet
        isOpen={showImageUpload}
        onClose={() => setShowImageUpload(false)}
        onUpload={handleImageUpload}
        title="Upload Project Image"
        description="Add a cover image for your project"
        currentImage={project.image}
      />

      {/* Edit Project Side Sheet */}
      <SideSheet
        isOpen={showEditProjectModal}
        onClose={() => {
          setShowEditProjectModal(false);
          setEditProjectName('');
          setError('');
        }}
        title="Edit Project"
        description="Update the project details"
        size="md"
        footer={
          <Button onClick={handleEditProject} isLoading={isSubmitting}>
            Save Changes
          </Button>
        }
      >
        <div className="space-y-4">
          <Input
            label="Project Name"
            placeholder="Enter project name"
            value={editProjectName}
            onChange={(e) => setEditProjectName(e.target.value)}
            error={error}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditProject();
            }}
          />
        </div>
      </SideSheet>

      {/* Admin Settings Side Sheet */}
      <SideSheet
        isOpen={showEmailDigestsSheet}
        onClose={() => {
          setShowEmailDigestsSheet(false);
          setDigestError('');
        }}
        title="Project Settings"
        description="Configure project digest settings"
        size="md"
      >
        <div className="space-y-4">
          {/* Member opt-out toggle */}
          <button
            onClick={() => handleUpdateDigestPreference(!digestOptedOut)}
            disabled={isDigestPrefSaving}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-colors ${
              digestOptedOut
                ? 'border-border bg-surface-light/50'
                : 'border-primary/30 bg-primary/5'
            } ${isDigestPrefSaving ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50 cursor-pointer'}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                digestOptedOut ? 'bg-surface-light border border-border' : 'bg-primary'
              }`}>
                {!digestOptedOut && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-medium text-text">Receive digest emails</p>
                <p className="text-xs text-muted mt-0.5">Get periodic summaries of project activity</p>
              </div>
            </div>
            {isDigestPrefSaving && (
              <svg className="w-4 h-4 text-muted animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </button>

          {/* Admin settings - direct, no collapsible wrapper */}
          {isAdmin && (
            <div className="space-y-4 pt-2 border-t border-border">
              {/* Enable/disable toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text">Enable digest emails</p>
                  <p className="text-xs text-muted mt-0.5">Send activity summaries to project members</p>
                </div>
                <button
                  onClick={() => setDigestEnabledDraft(!digestEnabledDraft)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                    digestEnabledDraft ? 'bg-primary' : 'bg-surface-light'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                    digestEnabledDraft ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Frequency selector */}
              <div>
                <label className="block text-sm font-medium text-muted mb-1.5">Frequency</label>
                <select
                  value={digestFrequencyDraft}
                  onChange={(e) => setDigestFrequencyDraft(Number(e.target.value))}
                  className="w-full h-9 rounded-md border border-input bg-secondary px-3 py-1 text-sm text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {digestFrequencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Next run info + save */}
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <p className="text-xs text-muted">
                  {digestConfig?.nextRunAt
                    ? <>Next run: <span className="text-text">{new Date(digestConfig.nextRunAt).toLocaleString()}</span></>
                    : 'Not scheduled'
                  }
                </p>
                <Button size="sm" onClick={handleSaveDigestSettings} isLoading={isDigestSaving}>
                  Save
                </Button>
              </div>
            </div>
          )}

          {digestError && (
            <p className="text-destructive text-sm flex items-center gap-1.5">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {digestError}
            </p>
          )}
        </div>
      </SideSheet>
    </div>
  );
}
